import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { observer } from 'mobx-lite';
import { Box, Stack, Typography, Chip, Avatar, Button } from '@mui/material';
import { Login, Logout } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import type { GuardResult } from '../../Stores/GuardModeStore';
import type { EventThemesStore } from '../../Stores/EventThemesStore';
import { resolveResultActions } from './resolveOutcome';

interface Props {
    result: GuardResult | null;
    visible: boolean;
    eventThemesStore: EventThemesStore;
    canIn?: boolean;
    canOut?: boolean;
    showIgnore?: boolean;
    onIn?: () => void;
    onIgnore?: () => void;
    onOut?: () => void;
    onOverlayActiveChanged?: (active: boolean) => void;
}

// Fallback border colors per outcome when no theme color is available.
const OUTCOME_FALLBACK: Record<string, string> = {
    granted: '34, 197, 94',       // green
    denied: '234, 179, 8',        // yellow
    unknown: '239, 68, 68',       // red
    'no-result': '156, 163, 175', // gray
};

/** Formats an entity-field DOB value to a plain date (no time component). */
function formatDob(raw: string): string {
    const datePart = raw.split('T')[0];
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (m) return `${m[2]}/${m[3]}/${m[1]}`; // MM/DD/YYYY, no timezone shift
    const d = new Date(raw);
    return isNaN(d.getTime()) ? raw : d.toLocaleDateString();
}

/** Shrinks the font size so the single-line text fits the container width. */
// eslint-disable-next-line react-refresh/only-export-components
const FitText: React.FC<{ text: string; color: string; max?: number; min?: number }> = ({ text, color, max = 56, min = 18 }) => {
    const ref = useRef<HTMLDivElement>(null);
    // Imperatively shrink the font (before paint) until the single line fits the
    // container width. Kept out of React state to avoid an extra render pass.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        let s = max;
        el.style.fontSize = `${s}px`;
        while (s > min && el.scrollWidth > el.clientWidth) {
            s -= 2;
            el.style.fontSize = `${s}px`;
        }
    }, [text, max, min]);
    return (
        <div
            ref={ref}
            style={{
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textAlign: 'center',
                fontSize: max,
                fontWeight: 800,
                color,
                textShadow: '0 2px 12px rgba(0,0,0,0.6)',
            }}
        >
            {text}
        </div>
    );
};

export const GuardResultOverlay: React.FC<Props> = observer(
    ({ result, visible, eventThemesStore, canIn, canOut, showIgnore, onIn, onIgnore, onOut, onOverlayActiveChanged }) => {
        const shouldShow = visible && !!result;

        const prevShouldShowRef = useRef(false);
        useEffect(() => {
            if (shouldShow && !prevShouldShowRef.current) onOverlayActiveChanged?.(true);
            prevShouldShowRef.current = shouldShow;
        }, [shouldShow, onOverlayActiveChanged]);

        // Color and text follow the OUTCOME. Matched outcomes (granted/denied) use the
        // configured theme; unknown/no-result use the neutral fallback so an unrelated
        // classification can't paint an unknown person with a matched theme's color.
        const outcome = result?.outcome ?? 'no-result';
        const actions = resolveResultActions({
            outcome,
            canIn: !!canIn,
            canOut: !!canOut,
            showIgnoreSetting: showIgnore ?? true,
        });
        const theme = result?.classificationValue
            ? eventThemesStore.getThemeByClassification(result.classificationValue)
            : null;
        const useThemeColor = (outcome === 'granted' || outcome === 'denied') && !!theme;
        const rgb = useThemeColor
            ? `${theme!.color.r}, ${theme!.color.g}, ${theme!.color.b}`
            : OUTCOME_FALLBACK[outcome];

        const themeText = useThemeColor
            ? (theme!.message || result?.classificationValue || '')
            : outcome === 'unknown'
                ? 'Unknown Person'
                : outcome === 'no-result'
                    ? 'No Result'
                    : (result?.classificationValue || '');
        const person = result?.person ?? null;

        // flex:1 + minWidth:0 lets the buttons share the row width and shrink to fit
        // narrow phone screens instead of overflowing to the edges.
        const actionBtnSx = { flex: 1, minWidth: 0, py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2 } as const;

        return (
            <AnimatePresence onExitComplete={() => onOverlayActiveChanged?.(false)}>
                {shouldShow && result && (
                    <motion.div
                        key="guard-result"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 1200,
                            background: 'rgba(8, 12, 18, 0.92)',
                            backdropFilter: 'blur(8px)',
                            border: `8px solid rgba(${rgb}, 1)`,
                            boxShadow: `inset 0 0 60px rgba(${rgb}, 0.45)`,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Stack spacing={2} sx={{ flex: 1, p: 3, alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                            <Box sx={{ width: '100%', px: 2 }}>
                                <FitText text={themeText} color={`rgb(${rgb})`} />
                            </Box>

                            {person && (
                                <>
                                    <Avatar
                                        src={person.enrollmentPhotoUrl ?? undefined}
                                        sx={{ width: 160, height: 160, border: `3px solid rgba(${rgb}, 0.7)` }}
                                    />
                                    <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{person.fullName}</Typography>
                                        {person.vendor && <Typography color="text.secondary">{person.vendor}</Typography>}
                                        {person.phone && <Typography color="text.secondary">{person.phone}</Typography>}
                                        {person.email && <Typography color="text.secondary">{person.email}</Typography>}
                                        {person.dob && <Typography color="text.secondary">DOB: {formatDob(person.dob)}</Typography>}
                                        {person.tags.length > 0 && (
                                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'center', pt: 1 }}>
                                                {person.tags.map((t) => (
                                                    <Chip key={t} label={t} size="small"
                                                        sx={{ backgroundColor: `rgba(${rgb}, 0.18)`, color: `rgb(${rgb})` }} />
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                </>
                            )}

                            {/* The guard must pick one of these to dismiss the result. In/Out record
                                the detection under the configured detector; Ignore just dismisses.
                                Which buttons appear is decided by resolveResultActions. */}
                            <Stack direction="row" spacing={1.5} sx={{ pt: 2, width: '100%' }}>
                                {actions.showIn && (
                                    <Button variant="contained" color="success" startIcon={<Login />}
                                        onClick={onIn} sx={actionBtnSx}>
                                        In
                                    </Button>
                                )}
                                {actions.showIgnore && (
                                    <Button variant="outlined" onClick={onIgnore}
                                        sx={{ ...actionBtnSx, color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>
                                        {actions.ignoreLabel}
                                    </Button>
                                )}
                                {actions.showOut && (
                                    <Button variant="contained" color="info" startIcon={<Logout />}
                                        onClick={onOut} sx={actionBtnSx}>
                                        Out
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </motion.div>
                )}
            </AnimatePresence>
        );
    },
);

GuardResultOverlay.displayName = 'GuardResultOverlay';
